import type { Collector } from '@leyan/tracing';
import { noop } from 'lodash-es';
import log from 'loglevel';
import jsonStringify from 'safe-stable-stringify';
import { scheduleIdle } from 'utils/scheduleIdle';

export interface ContainerMeta {
  appName: string;
  procName: string;
  containerId?: string;
}

export interface LogEntry {
  level: string;
  message: string;
  loggerName: string;
  timeMillis: number;
  contextMap?: Record<string, string>;
  containerMeta: ContainerMeta;
}

const APP_NAME = 'oversea-fett';
const PROC_NAME = 'web';
const LOG_URL = import.meta.env.VITE_LOGGER_URL;

let _batchTimeoutID: NodeJS.Timeout | undefined;
const _batchLogs: any[] = [];

let _baseContext: Record<string, string> = {};

export const setLoggerBaseContext = (context: Record<string, string>) => {
  _baseContext = context;
};

function format(level: string, message: string, contextMap: Record<string, string> = {}): LogEntry {
  const log: LogEntry = {
    timeMillis: new Date().getTime(),
    level: level.toUpperCase(),
    message: message,
    loggerName: APP_NAME,
    contextMap: {
      ...contextMap,
      ..._baseContext,
      userAgent: window.navigator.userAgent || '',
      platform: window.navigator.platform || '',
      language: window.navigator.language || '',
    },
    containerMeta: {
      appName: APP_NAME,
      procName: PROC_NAME,
    },
  };
  return log;
}

function doBatchRequest() {
  if (_batchTimeoutID !== undefined) {
    clearTimeout(_batchTimeoutID);
    _batchTimeoutID = undefined;
  }

  const body = _batchLogs.map((log) => jsonStringify(log)).join('\n');
  _batchLogs.length = 0;

  if (body && fetch) {
    fetch(LOG_URL, {
      method: 'POST',
      body,
    }).catch(noop);
  }
}

function sendLogToServer(level: string, message: string, contextMap?: Record<string, string>) {
  const log = format(level, message, contextMap);
  _batchLogs.push(log);

  if (_batchLogs.length === 1) {
    _batchTimeoutID = setTimeout(() => {
      doBatchRequest();
    }, 5000);
  }
  if (_batchLogs.length === 100) {
    doBatchRequest();
  }
}

export const formatLoggerData = (data: any) => {
  return jsonStringify(data);
};

export const logger = {
  info: (message: string, contextMap?: Record<string, string>) => {
    scheduleIdle(() => {
      console.info(message, contextMap);
      log.info(message, contextMap);
      sendLogToServer('info', message, contextMap);
    });
  },
  error: (message: string, contextMap?: Record<string, string>) => {
    scheduleIdle(() => {
      log.error(message, contextMap);
      sendLogToServer('error', message, contextMap);
    });
  },
  warn: (message: string, contextMap?: Record<string, string>) => {
    scheduleIdle(() => {
      log.warn(message, contextMap);
      sendLogToServer('warn', message, contextMap);
    });
  },
  debug: (message: string, contextMap?: Record<string, string>) => {
    scheduleIdle(() => {
      log.debug(message, contextMap);
      sendLogToServer('debug', message, contextMap);
    });
  },
  trace: (message: string, contextMap?: Record<string, string>) => {
    scheduleIdle(() => {
      log.trace(message, contextMap);
      sendLogToServer('trace', message, contextMap);
    });
  },
};

export const loggerCollector: Collector = {
  event(meta) {
    const { name, level, message, params, attributes, traceId, spanId, spanName } = meta;

    if (
      (name === '@leyan/sg-web-report/remote' || name === '@leyan/sg-web-report/publish') &&
      (level === 'info' || level === 'warn' || level === 'error')
    ) {
      sendLogToServer(level, message, {
        $name: name,
        ...(traceId && {
          $traceId: traceId,
        }),
        ...(spanId && {
          $spanId: spanId,
        }),
        ...(spanName && {
          $spanName: spanName,
        }),
        ...params.reduce<Record<string, string>>((result, param, index) => {
          let value;

          if (param instanceof Error) {
            value = param.message;
          } else if (typeof param === 'string') {
            value = param;
          } else {
            try {
              value = JSON.stringify(param);
            } catch {
              value = String(param);
            }
          }

          result[`$param_${index}`] = value;

          return result;
        }, {}),
        ...Object.entries(attributes).reduce<Record<string, string>>((result, [key, value]) => {
          if (value !== undefined && value !== null) {
            result[key] = String(value);
          }

          return result;
        }, {}),
      });
    }
  },
};
